import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import CheckoutButton from "@/components/CheckoutButton";
import SamplePreviewButton from "@/components/SamplePreviewModal";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG = "/owner.jpg";

const MaskLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Particles = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let w, h, raf;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const dots = Array.from({ length: 64 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.6 + 0.4,
      s: Math.random() * 0.00035 + 0.00012,
      o: Math.random() * 0.3 + 0.08,
      gold: Math.random() > 0.5,
    }));
    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);
    const tick = (t) => {
      ctx.clearRect(0, 0, w, h);
      dots.forEach((d) => {
        const y = (d.y - t * d.s + 1) % 1;
        ctx.beginPath();
        ctx.arc(d.x * w, y * h, d.r, 0, Math.PI * 2);
        ctx.fillStyle = d.gold
          ? `rgba(212,175,55,${d.o})`
          : `rgba(16,185,129,${d.o * 0.75})`;
        ctx.fill();
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full" aria-hidden="true" />;
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden grain">
      <Particles />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 50% at 75% 30%, rgba(212,175,55,0.09), transparent 70%), radial-gradient(50% 40% at 15% 80%, rgba(16,185,129,0.07), transparent 70%)",
        }}
      />
      <motion.div
        style={{ opacity: fade }}
        className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-20 lg:pt-24 w-full grid lg:grid-cols-12 gap-12 lg:gap-10 items-center"
      >
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="flex items-center gap-3 mb-7"
          >
            <span className="h-px w-10 bg-gold/60" />
            <p className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-gold">
              All In Hand Marketing
            </p>
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            <MaskLine delay={0.2}>Everything your business</MaskLine>
            <MaskLine delay={0.34}>{'needs to grow\u00A0—'}</MaskLine>
            <MaskLine delay={0.48}>
              <span className="text-gold-gradient">All In Hand.</span>
            </MaskLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-7"
          >
            <p className="text-white font-medium text-lg sm:text-xl leading-snug max-w-xl" data-testid="hero-offer-subline">
              Find out what's stopping your business from getting more visibility, leads and
              conversions.
            </p>
            <p className="text-slate-300 mt-2 text-base sm:text-lg">
              Get your Business Growth Assessment for{" "}
              <span className="text-gold font-display font-bold">$99.99</span>.
            </p>
            <p className="text-slate-500 mt-4 text-sm leading-relaxed max-w-xl">
              All In Hand Marketing helps entrepreneurs, small businesses, organizations, creators,
              and growing brands turn ideas into measurable digital business systems.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.92 }}
            className="flex flex-wrap items-center gap-4 mt-8"
          >
            <CheckoutButton
              lookup="aih_full_assessment"
              label="Get My Assessment — $99.99"
              testid="hero-cta-assessment"
              className="px-7 py-4 rounded-full bg-gold text-obsidian font-display font-bold text-sm sm:text-base hover:bg-gold-bright hover:-translate-y-0.5 transition-all duration-300 gold-ring"
            />
            <Link
              to="/intake?type=consultation"
              data-testid="hero-cta-consultation"
              className="px-7 py-4 rounded-full border border-slate-600 text-slate-200 font-semibold text-sm sm:text-base hover:border-gold hover:text-gold transition-colors duration-300"
            >
              Book a Consultation
            </Link>
            <SamplePreviewButton
              testid="hero-preview-sample"
              className="px-7 py-4 rounded-full border border-slate-600 text-slate-300 font-semibold text-sm sm:text-base hover:border-gold hover:text-gold transition-colors duration-300"
            />
          </motion.div>

          <motion.button
            data-testid="hero-cta-packages"
            onClick={() => scrollToId("packages")}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-6 text-sm text-slate-400 hover:text-gold underline underline-offset-4 decoration-gold/40 transition-colors"
          >
            Explore Marketing Packages
          </motion.button>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600"
          >
            7-Day Money-Back Guarantee · Secure Checkout · Human Support
          </motion.p>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div style={{ y: photoY }} className="relative max-w-md mx-auto lg:ml-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="absolute -inset-4 sm:-inset-8 bg-gold/10 blur-3xl rounded-full pointer-events-none" />
              <div className="clip-arch overflow-hidden border border-gold/30 gold-ring relative">
                <img
                  src={HERO_IMG}
                  alt="The owner of All In Hand Marketing"
                  className="w-full h-[420px] lg:h-[560px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 via-transparent to-transparent" />
              </div>
              <div className="absolute -left-4 sm:-left-10 bottom-10 glass-card rounded-2xl px-5 py-4 animate-float-slow">
                <p className="font-mono text-[10px] tracking-[0.3em] text-emerald-400 uppercase">
                  The System
                </p>
                <p className="font-display font-bold text-white mt-1 text-sm">
                  Assess · Build · Grow · Scale
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
